import React, { useState, useEffect } from 'react';
import { Conta, ContaAnexo, TipoAnexoId } from '../../types/financeiro';
import { parseContaObs } from '../../utils/financeiroMetadataUtils';
import { financeiroService } from '../../services/financeiroService';
import { sanitizeText } from '../../utils/textSanitizer';
import { 
  X, 
  Download, 
  FileText, 
  ExternalLink, 
  Receipt, 
  CheckCircle2, 
  FileCode, 
  CreditCard,
  FileCheck,
  Layers,
  Paperclip
} from 'lucide-react';

interface VisualizarComprovanteModalProps {
  isOpen: boolean;
  onClose: () => void;
  conta: Conta | null;
  pessoaNome?: string;
}

interface DisplayDoc {
  id: number | string;
  tipo_id: number;
  tipo_nome: string;
  nome: string;
  url: string;
}

export function VisualizarComprovanteModal({
  isOpen,
  onClose,
  conta,
  pessoaNome = ''
}: VisualizarComprovanteModalProps) {
  const [docs, setDocs] = useState<DisplayDoc[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && conta) {
      loadAllDocuments(conta);
    } else {
      setDocs([]);
      setSelectedIdx(0);
    }
  }, [isOpen, conta]);

  const loadAllDocuments = async (c: Conta) => {
    setLoading(true);
    const docList: DisplayDoc[] = [];
    const seenUrls = new Set<string>();

    try {
      const dbAnexos = await financeiroService.getAnexosConta(c.id);
      dbAnexos.forEach((a) => {
        if (a.arquivo && !seenUrls.has(a.arquivo.trim())) {
          seenUrls.add(a.arquivo.trim());
          docList.push({
            id: a.id,
            tipo_id: a.tipo_anexo_id,
            tipo_nome: a.tipo_anexo?.nome || (
              a.tipo_anexo_id === TipoAnexoId.BOLETO ? 'Boleto' :
              a.tipo_anexo_id === TipoAnexoId.NOTA_FISCAL ? 'Nota Fiscal' :
              a.tipo_anexo_id === TipoAnexoId.COMPROVANTE ? 'Comprovante' :
              a.tipo_anexo_id === TipoAnexoId.RECIBO ? 'Recibo' :
              a.tipo_anexo_id === TipoAnexoId.FATURA ? 'Fatura' : 'Outro'
            ),
            nome: a.descricao || 'Documento Anexo',
            url: a.arquivo
          });
        }
      });
    } catch (err) {
      console.warn('Erro ao carregar anexos:', err);
    }

    // Fallback checks from conta and obs
    const meta = parseContaObs(c.obs);

    if (c.boleto_url && !seenUrls.has(c.boleto_url.trim())) {
      seenUrls.add(c.boleto_url.trim());
      docList.push({
        id: 'boleto-conta',
        tipo_id: TipoAnexoId.BOLETO,
        tipo_nome: 'Boleto Bancário',
        nome: c.boleto_nome || 'Boleto Bancário',
        url: c.boleto_url
      });
    }

    if (c.nf_url && !seenUrls.has(c.nf_url.trim())) {
      seenUrls.add(c.nf_url.trim());
      docList.push({
        id: 'nf-conta',
        tipo_id: TipoAnexoId.NOTA_FISCAL,
        tipo_nome: 'Nota Fiscal',
        nome: c.nf_nome || `Nota Fiscal ${c.numero_nf || ''}`.trim(),
        url: c.nf_url
      });
    }

    const compUrl = c.comprovante_url || meta.comprovante_url;
    if (compUrl && !seenUrls.has(compUrl.trim())) {
      seenUrls.add(compUrl.trim());
      docList.push({
        id: 'comp-conta',
        tipo_id: TipoAnexoId.COMPROVANTE,
        tipo_nome: 'Comprovante de Pagamento',
        nome: c.comprovante_nome || meta.comprovante_nome || 'Comprovante de Pagamento',
        url: compUrl
      });
    }

    setDocs(docList);
    setSelectedIdx(0);
    setLoading(false);
  };

  if (!isOpen || !conta) return null;

  const currentDoc = docs[selectedIdx] || null;
  const currentUrl = currentDoc?.url || '';
  const currentNome = currentDoc?.nome || 'documento';
  const isImage = currentUrl.startsWith('data:image/') || currentUrl.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i);
  const isPdf = currentUrl.startsWith('data:application/pdf') || currentUrl.match(/\.pdf($|\?)/i);

  const handleDownload = () => {
    if (!currentUrl) return;
    const a = document.createElement('a');
    a.href = currentUrl;
    a.download = currentNome.includes('.') ? currentNome : `${currentNome}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const getDocIcon = (tipoId: number) => {
    switch (tipoId) {
      case TipoAnexoId.BOLETO:
        return FileCode;
      case TipoAnexoId.NOTA_FISCAL:
        return Receipt;
      case TipoAnexoId.COMPROVANTE:
        return CheckCircle2;
      case TipoAnexoId.RECIBO:
        return FileCheck;
      case TipoAnexoId.FATURA:
        return CreditCard;
      default:
        return FileText;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col border border-slate-200 animate-in fade-in zoom-in duration-200 max-h-[92vh]">
        
        {/* Header */}
        <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-slate-800 rounded-lg text-emerald-400">
              <Paperclip className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold">Documentos & Anexos da Conta</h3>
              <p className="text-xs text-slate-400">
                Lançamento #{conta.id} • {sanitizeText(pessoaNome)} • {docs.length} {docs.length === 1 ? 'documento' : 'documentos'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {currentUrl && (
              <button
                type="button"
                onClick={handleDownload}
                className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors cursor-pointer"
                title="Baixar Documento Atual"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Multiple Documents Selector Tabs */}
        {docs.length > 1 && (
          <div className="bg-slate-100 px-6 py-2.5 border-b border-slate-200 flex items-center gap-2 overflow-x-auto">
            <span className="text-[11px] font-bold text-slate-500 uppercase flex-shrink-0">
              Anexos:
            </span>
            {docs.map((doc, idx) => {
              const Icon = getDocIcon(doc.tipo_id);
              const isSelected = selectedIdx === idx;
              return (
                <button
                  key={`doc-tab-${doc.id}-${idx}`}
                  type="button"
                  onClick={() => setSelectedIdx(idx)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex-shrink-0 border ${
                    isSelected
                      ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-emerald-400' : 'text-slate-500'}`} />
                  <span className="max-w-[140px] truncate">{doc.tipo_nome}: {doc.nome}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Info Bar */}
        <div className="bg-slate-50 px-6 py-2.5 border-b border-slate-200 grid grid-cols-3 gap-2 text-xs flex-shrink-0">
          <div>
            <span className="text-slate-500 font-medium block text-[11px]">Tipo de Documento:</span>
            <span className="font-bold text-slate-800 truncate block">{currentDoc?.tipo_nome || '-'}</span>
          </div>
          <div>
            <span className="text-slate-500 font-medium block text-[11px]">Nº Documento / NF:</span>
            <span className="font-bold text-slate-800">{sanitizeText(conta.numero_nf) || '-'}</span>
          </div>
          <div>
            <span className="text-slate-500 font-medium block text-[11px]">Valor da Conta:</span>
            <span className="font-bold text-slate-900">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(conta.valor_pago || conta.valor)}
            </span>
          </div>
        </div>

        {/* Content Viewer */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 flex flex-col items-center justify-center bg-slate-100/50 min-h-[350px]">
          {loading ? (
            <div className="text-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-2"></div>
              <p className="text-xs text-slate-500">Carregando documentos...</p>
            </div>
          ) : currentUrl ? (
            isImage ? (
              <div className="w-full flex justify-center">
                <img
                  src={currentUrl}
                  alt={currentNome}
                  className="max-h-[60vh] max-w-full rounded-xl object-contain border border-slate-300 shadow-md bg-white"
                />
              </div>
            ) : isPdf ? (
              <div className="w-full h-[60vh] flex flex-col items-center justify-center">
                <iframe
                  src={currentUrl}
                  title={currentNome}
                  className="w-full h-full rounded-xl border border-slate-300 shadow-xs"
                />
              </div>
            ) : (
              <div className="text-center p-8 bg-white rounded-2xl border border-slate-200 shadow-xs max-w-md">
                <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <h4 className="text-sm font-bold text-slate-800 mb-1">{currentNome}</h4>
                <p className="text-xs text-slate-500 mb-4">
                  O arquivo está pronto. Clique abaixo para abrir no navegador ou fazer o download.
                </p>
                <div className="flex justify-center gap-2">
                  <a
                    href={currentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> Abrir no Navegador
                  </a>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
                  >
                    <Download className="w-4 h-4" /> Baixar
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="text-center p-8">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-600">Nenhum anexo encontrado para esta conta.</p>
              <p className="text-xs text-slate-400 mt-1">
                Você pode anexar Boletos, Notas Fiscais e Comprovantes ao editar ou dar baixa na conta.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-white border-t border-slate-200 flex items-center justify-between flex-shrink-0">
          <div className="text-xs text-slate-500 font-medium">
            {docs.length > 0 ? (
              <span>Documento {selectedIdx + 1} de {docs.length}</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {currentUrl && (
              <a
                href={currentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Abrir em Nova Aba
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
            >
              Fechar
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
